import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  User,
  Mail,
  Phone,
  Building2,
  Briefcase,
  Award,
  Loader2,
  XCircle,
  Target,
  Mic,
  Square,
  Play,
  Pause,
  RotateCcw,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import logo from "../public/logo.png";

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
  }
}

interface FormData {
  fullName: string;
  email: string;
  phone: string;
  ageRange: string;
  canWorkFullTimeInOffice: string;
  comfortableCommissionRole: string;
  canWorkEveningsSaturdays: string;
  licenseStatus: string;
  licensingEligibility: string;
  targetWeeklyIncome: string;
  salesExperience: string;
  hasNoSalesExperience: boolean;
  rejectionHandling: string;
  commissionMotivation: string;
  voiceMessageLink: string;
  disclaimer: boolean;
}

function splitFullName(fullName: string): { firstName: string; lastName: string } {
  const trimmed = fullName.trim();
  const parts = trimmed.split(/\s+/);
  if (parts.length === 0) {
    return { firstName: "", lastName: "" };
  }
  if (parts.length === 1) {
    return { firstName: parts[0] || "", lastName: "" };
  }
  const firstName = parts[0] || "";
  const lastName = parts.slice(1).join(" ");
  return { firstName, lastName };
}

function getLeadSource(): string {
  const params = new URLSearchParams(window.location.search);
  return params.get("utm_source") || params.get("source") || params.get("h") || params.get("headline") || "direct";
}

const ageRangeOptions = [
  { value: "18-21", label: "18-21" },
  { value: "22-30", label: "22-30" },
  { value: "31-39", label: "31-39" },
  { value: "40+", label: "40+" }
];

const yesNoOptions = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" }
];

const licenseStatusOptions = [
  { value: "active", label: "Yes - Active" },
  { value: "expired", label: "Yes - Expired" },
  { value: "none", label: "No" }
];

const targetWeeklyIncomeOptions = [
  { value: "3000+", label: "$3,000+" },
  { value: "2000-3000", label: "$2,000-$3,000" },
  { value: "1500-2000", label: "$1,500-$2,000" },
  { value: "under-1500", label: "Under $1,500" }
];

const steps = [
  { id: 1, title: "Contact", icon: User },
  { id: 2, title: "Work", icon: Building2 },
  { id: 3, title: "Basics", icon: Target },
  { id: 4, title: "Sales", icon: Briefcase },
  { id: 5, title: "Mindset", icon: Target },
  { id: 6, title: "Final", icon: Award }
];

function formatPhoneNumber(value: string): string {
  let digits = value.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    digits = digits.slice(1);
  }
  digits = digits.slice(0, 10);
  if (digits.length === 0) return "";
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function isValidPhone(phone: string): boolean {
  return phone.replace(/\D/g, "").length === 10;
}

function focusNextInput(e: React.KeyboardEvent<HTMLInputElement>) {
  if (e.key === "Enter") {
    e.preventDefault();
    const form = e.currentTarget.form;
    if (!form) return;
    const inputs = Array.from(form.querySelectorAll("input:not([type=hidden])"));
    const currentIndex = inputs.indexOf(e.currentTarget);
    const nextInput = inputs[currentIndex + 1] as HTMLInputElement | undefined;
    if (nextInput) {
      nextInput.focus();
    } else {
      e.currentTarget.blur();
    }
  }
}

const STORAGE_KEY = "empire-application-draft";

export function Apply() {
  const minRecordingSeconds = 30;
  const maxRecordingSeconds = 60;
  const leadSource = getLeadSource();

  // Load saved form data from localStorage
  const getInitialFormData = (): FormData => {
    if (typeof window === "undefined") {
      return {
        fullName: "",
        email: "",
        phone: "",
        ageRange: "",
        canWorkFullTimeInOffice: "",
        comfortableCommissionRole: "",
        canWorkEveningsSaturdays: "",
        licenseStatus: "",
        licensingEligibility: "",
        targetWeeklyIncome: "",
        salesExperience: "",
        hasNoSalesExperience: false,
        rejectionHandling: "",
        commissionMotivation: "",
        voiceMessageLink: "",
        disclaimer: false
      };
    }
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          fullName: parsed.fullName || "",
          email: parsed.email || "",
          phone: parsed.phone || "",
          ageRange: parsed.ageRange || "",
          canWorkFullTimeInOffice: parsed.canWorkFullTimeInOffice || "",
          comfortableCommissionRole: parsed.comfortableCommissionRole || "",
          canWorkEveningsSaturdays: parsed.canWorkEveningsSaturdays || "",
          licenseStatus: parsed.licenseStatus || "",
          licensingEligibility: parsed.licensingEligibility || "",
          targetWeeklyIncome: parsed.targetWeeklyIncome || "",
          salesExperience: parsed.salesExperience || "",
          hasNoSalesExperience: parsed.hasNoSalesExperience || false,
          rejectionHandling: parsed.rejectionHandling || "",
          commissionMotivation: parsed.commissionMotivation || "",
          voiceMessageLink: parsed.voiceMessageLink || "",
          disclaimer: false
        };
      }
    } catch {
      // Ignore parse errors
    }
    return {
      fullName: "",
      email: "",
      phone: "",
      ageRange: "",
      canWorkFullTimeInOffice: "",
      comfortableCommissionRole: "",
      canWorkEveningsSaturdays: "",
      licenseStatus: "",
      licensingEligibility: "",
      targetWeeklyIncome: "",
      salesExperience: "",
      hasNoSalesExperience: false,
      rejectionHandling: "",
      commissionMotivation: "",
      voiceMessageLink: "",
      disclaimer: false
    };
  };

  const getInitialStep = (): number => {
    if (typeof window === "undefined") return 1;
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed.currentStep || 1;
      }
    } catch {
      // Ignore parse errors
    }
    return 1;
  };

  // ALL useState declarations FIRST (before any useEffect)
  const [formData, setFormData] = useState<FormData>(getInitialFormData);
  const [currentStep, setCurrentStep] = useState(getInitialStep);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDisqualified, setIsDisqualified] = useState(false);
  const [disqualifyReason, setDisqualifyReason] = useState("");
  const [direction, setDirection] = useState(1);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [isUploadingVoice, setIsUploadingVoice] = useState(false);
  const [voicePreviewUrl, setVoicePreviewUrl] = useState("");
  const [voiceUploadError, setVoiceUploadError] = useState("");
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackProgress, setPlaybackProgress] = useState(0);
  const [pendingVoiceBlob, setPendingVoiceBlob] = useState<Blob | null>(null);
  const [pendingVoiceMimeType, setPendingVoiceMimeType] = useState<string>("");

  // ALL useRef declarations
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const hasSubmitted = useRef(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const recordingChunksRef = useRef<Blob[]>([]);
  const recordingIntervalRef = useRef<number | null>(null);
  const recordingSecondsRef = useRef(0);

  const clearSavedFormData = () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Ignore storage errors
    }
  };

  // Save form data to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        ...formData,
        currentStep
      }));
    } catch {
      // Ignore storage errors (e.g., private browsing)
    }
  }, [formData, currentStep]);

  // Send partial application data when user leaves without completing
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (hasSubmitted.current || isDisqualified) return;

      const hasUsefulData =
        formData.fullName.trim() || formData.email.trim() || formData.phone.trim();
      if (!hasUsefulData) return;

      const { firstName, lastName } = splitFullName(formData.fullName);
      const payload = {
        firstName,
        lastName,
        email: formData.email,
        phone: formData.phone,
        ageRange: formData.ageRange,
        canWorkFullTimeInOffice: formData.canWorkFullTimeInOffice,
        comfortableCommissionRole: formData.comfortableCommissionRole,
        canWorkEveningsSaturdays: formData.canWorkEveningsSaturdays,
        licenseStatus: formData.licenseStatus,
        licensingEligibility: formData.licensingEligibility,
        targetWeeklyIncome: formData.targetWeeklyIncome,
        salesExperience: formData.salesExperience,
        hasNoSalesExperience: formData.hasNoSalesExperience,
        rejectionHandling: formData.rejectionHandling,
        commissionMotivation: formData.commissionMotivation,
        voiceMessageLink: formData.voiceMessageLink,
        currentStep,
        leadSource,
      };

      navigator.sendBeacon(
        "/api/partial-application",
        new Blob([JSON.stringify(payload)], { type: "application/json" })
      );
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  });

  // Track form step progression in Google Analytics
  // Using separate events per step so data shows in GA without custom dimension config
  useEffect(() => {
    if (typeof window.gtag === "function") {
      const stepNames: Record<number, string> = {
        1: "form_step_1_contact",
        2: "form_step_2_work",
        3: "form_step_3_age",
        4: "form_step_4_experience",
        5: "form_step_5_motivation",
        6: "form_step_6_voice",
      };
      const eventName = stepNames[currentStep] || `form_step_${currentStep}`;
      window.gtag("event", eventName);

      // Also keep the combined event for backward compatibility
      const stepTitle = steps[currentStep - 1]?.title || "";
      window.gtag("event", "form_step_view", {
        step_number: currentStep,
        step_title: stepTitle,
      });
    }
  }, [currentStep]);

  useEffect(() => {
    const scriptId = "trustedform-script";
    if (document.getElementById(scriptId)) return;

    const tf = document.createElement("script");
    tf.id = scriptId;
    tf.type = "text/javascript";
    tf.async = true;

    const protocol = document.location.protocol === "https:" ? "https" : "http";
    tf.src = `${protocol}://api.trustedform.com/trustedform.js?field=xxTrustedFormCertUrl&use_tagged_consent=true&l=${Date.now()}${Math.random()}`;

    const firstScript = document.getElementsByTagName("script")[0];
    if (firstScript?.parentNode) {
      firstScript.parentNode.insertBefore(tf, firstScript);
    } else {
      document.head.appendChild(tf);
    }
  }, []);

  const updateFormData = (field: keyof FormData, value: string | string[] | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    updateFormData("phone", formatted);
  };

  const clearRecordingTimer = () => {
    if (recordingIntervalRef.current !== null) {
      window.clearInterval(recordingIntervalRef.current);
      recordingIntervalRef.current = null;
    }
  };

  const stopMediaStream = () => {
    mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
    mediaStreamRef.current = null;
  };

  const resetVoicePreview = () => {
    if (voicePreviewUrl) {
      URL.revokeObjectURL(voicePreviewUrl);
      setVoicePreviewUrl("");
    }
  };

  const getVoiceFileExtension = (mimeType: string): string => {
    if (mimeType.includes("mp4")) return "mp4";
    if (mimeType.includes("ogg")) return "ogg";
    if (mimeType.includes("mpeg")) return "mp3";
    return "webm";
  };

  const uploadVoiceRecording = async (blob: Blob, mimeType: string): Promise<string | null> => {
    setIsUploadingVoice(true);
    setVoiceUploadError("");

    try {
      const normalizedMimeType = mimeType || blob.type || "audio/webm";
      const extension = getVoiceFileExtension(normalizedMimeType);
      const file = new File([blob], `voice-message.${extension}`, { type: normalizedMimeType });
      let playbackUrl = "";

      try {
        const signedUrlResponse = await fetch("/api/voice-upload-url", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            fileName: file.name,
            contentType: normalizedMimeType,
          }),
        });

        const signedUrlResult = await signedUrlResponse.json();
        if (!signedUrlResponse.ok || !signedUrlResult.uploadUrl || !signedUrlResult.playbackUrl) {
          throw new Error(signedUrlResult.error || "Failed to create voice upload");
        }

        const uploadResponse = await fetch(signedUrlResult.uploadUrl, {
          method: "PUT",
          headers: {
            "Content-Type": normalizedMimeType,
          },
          body: file,
        });

        if (!uploadResponse.ok) {
          throw new Error("Direct voice upload failed");
        }

        playbackUrl = signedUrlResult.playbackUrl;
      } catch {
        const formData = new FormData();
        formData.append("file", file);

        const fallbackResponse = await fetch("/api/upload-voice-message", {
          method: "POST",
          body: formData,
        });

        const fallbackResult = await fallbackResponse.json();
        if (!fallbackResponse.ok || !fallbackResult.url) {
          throw new Error(fallbackResult.error || "Voice upload failed");
        }

        playbackUrl = fallbackResult.url;
      }

      updateFormData("voiceMessageLink", playbackUrl);
      setPendingVoiceBlob(null);
      setPendingVoiceMimeType("");
      return playbackUrl;
    } catch (error) {
      updateFormData("voiceMessageLink", "");
      setVoiceUploadError(error instanceof Error ? error.message : "Voice upload failed");
      return null;
    } finally {
      setIsUploadingVoice(false);
    }
  };

  const stopVoiceRecording = () => {
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop();
    }
  };

  const startVoiceRecording = async () => {
    if (typeof window === "undefined" || !navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setVoiceUploadError("This browser does not support in-app audio recording.");
      if (typeof window.gtag === "function") {
        window.gtag("event", "voice_recording_unsupported");
      }
      return;
    }

    // Track recording start
    if (typeof window.gtag === "function") {
      window.gtag("event", "voice_recording_started");
    }

    try {
      resetVoicePreview();
      setVoiceUploadError("");
      updateFormData("voiceMessageLink", "");
      setPendingVoiceBlob(null);
      setPendingVoiceMimeType("");
      setRecordingSeconds(0);
      recordingSecondsRef.current = 0;

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      const mimeTypeCandidates = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/mp4",
        "audio/ogg;codecs=opus",
      ];
      const selectedMimeType =
        mimeTypeCandidates.find((candidate) => MediaRecorder.isTypeSupported(candidate)) || "";

      const recorder = selectedMimeType
        ? new MediaRecorder(stream, { mimeType: selectedMimeType })
        : new MediaRecorder(stream);

      mediaRecorderRef.current = recorder;
      recordingChunksRef.current = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordingChunksRef.current.push(event.data);
        }
      };

      recorder.onerror = () => {
        setVoiceUploadError("Recording failed. Please try again.");
        clearRecordingTimer();
        stopMediaStream();
        setIsRecording(false);
      };

      recorder.onstop = () => {
        clearRecordingTimer();
        stopMediaStream();
        setIsRecording(false);

        const elapsedSeconds = recordingSecondsRef.current;
        const recordedMimeType = recorder.mimeType || selectedMimeType || "audio/webm";
        const blob = new Blob(recordingChunksRef.current, { type: recordedMimeType });
        const previewUrl = URL.createObjectURL(blob);
        setVoicePreviewUrl(previewUrl);

        // Store the blob for later upload when user is ready
        setPendingVoiceBlob(blob);
        setPendingVoiceMimeType(recordedMimeType);
        updateFormData("voiceMessageLink", ""); // Clear any previous upload

        // Track recording completion
        if (typeof window.gtag === "function") {
          window.gtag("event", "voice_recording_completed", {
            recording_seconds: elapsedSeconds,
          });
        }
      };

      recorder.start(250);
      setIsRecording(true);

      recordingIntervalRef.current = window.setInterval(() => {
        recordingSecondsRef.current += 1;
        setRecordingSeconds(recordingSecondsRef.current);

        if (recordingSecondsRef.current >= maxRecordingSeconds) {
          stopVoiceRecording();
        }
      }, 1000);
    } catch (error) {
      stopMediaStream();
      clearRecordingTimer();
      setIsRecording(false);
      setVoiceUploadError(error instanceof Error ? error.message : "Unable to access microphone.");
    }
  };

  useEffect(() => {
    return () => {
      clearRecordingTimer();
      stopMediaStream();
      resetVoicePreview();
    };
  }, [voicePreviewUrl]);

  const checkDisqualifiers = (): { disqualified: boolean; reason: string } => {
    if (formData.canWorkFullTimeInOffice === "no") {
      return {
        disqualified: true,
        reason: "This role requires full-time, in-office availability in Oakland Park. We appreciate your interest."
      };
    }
    if (formData.comfortableCommissionRole === "no") {
      return {
        disqualified: true,
        reason: "This role is 100% commission-based. If that compensation structure is not a fit right now, we appreciate your interest."
      };
    }
    if (formData.canWorkEveningsSaturdays === "no") {
      return {
        disqualified: true,
        reason: "This role requires evening and Saturday availability. We appreciate your interest."
      };
    }
    return { disqualified: false, reason: "" };
  };

  const nextStep = () => {
    if (currentStep === 1 && !validateStep1()) {
      return;
    }

    const { disqualified, reason } = checkDisqualifiers();
    if (disqualified) {
      setDisqualifyReason(reason);
      setIsDisqualified(true);
      return;
    }

    if (currentStep < steps.length) {
      setDirection(1);
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setDirection(-1);
      setCurrentStep(prev => prev - 1);
    }
  };

  const validateStep1 = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    const { firstName } = splitFullName(formData.fullName);
    if (!formData.fullName.trim()) {
      newErrors.fullName = "Name is required";
    } else if (!firstName) {
      newErrors.fullName = "Please enter your name";
    } else if (formData.fullName.length > 100) {
      newErrors.fullName = "Name is too long";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone is required";
    } else if (!isValidPhone(formData.phone)) {
      newErrors.phone = "Please enter a valid 10-digit phone";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        const { firstName } = splitFullName(formData.fullName);
        return !!firstName &&
               isValidEmail(formData.email) &&
               isValidPhone(formData.phone);
      case 2:
        return formData.canWorkFullTimeInOffice !== "" &&
               formData.comfortableCommissionRole !== "" &&
               formData.canWorkEveningsSaturdays !== "";
      case 3:
        return formData.ageRange !== "" && formData.targetWeeklyIncome !== "";
      case 4:
        return formData.hasNoSalesExperience || formData.salesExperience.trim().length > 0;
      case 5:
        return formData.rejectionHandling.trim().length > 0 &&
               formData.commissionMotivation.trim().length > 0;
      case 6:
        // Valid if recording is uploaded OR if there's a pending recording ready to upload
        return formData.voiceMessageLink.trim().length > 0 || !!pendingVoiceBlob;
      default:
        return false;
    }
  };

  const handleSubmit = async () => {
    const { disqualified, reason } = checkDisqualifiers();
    if (disqualified) {
      setDisqualifyReason(reason);
      setIsDisqualified(true);
      return;
    }

    setIsSubmitting(true);
    try {
      // Upload voice recording first if there's a pending blob
      let voiceLink = formData.voiceMessageLink;
      if (pendingVoiceBlob && pendingVoiceMimeType && !voiceLink) {
        const uploadedVoiceLink = await uploadVoiceRecording(pendingVoiceBlob, pendingVoiceMimeType);
        if (!uploadedVoiceLink) {
          setIsSubmitting(false);
          return;
        }
        voiceLink = uploadedVoiceLink;
      }

      const { firstName, lastName } = splitFullName(formData.fullName);

      // Get TrustedForm certificate URL from the global hidden field in index.html
      const trustedFormCertUrl = (document.getElementById("xxTrustedFormCertUrl") as HTMLInputElement)?.value || "";

      console.log("TrustedForm Debug:", { trustedFormCertUrl });

      const response = await fetch("/api/submit-application", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName,
          lastName,
          email: formData.email,
          phone: formData.phone,
          ageRange: formData.ageRange,
          canWorkFullTimeInOffice: formData.canWorkFullTimeInOffice,
          comfortableCommissionRole: formData.comfortableCommissionRole,
          canWorkEveningsSaturdays: formData.canWorkEveningsSaturdays,
          licenseStatus: formData.licenseStatus,
          licensingEligibility: formData.licensingEligibility,
          targetWeeklyIncome: formData.targetWeeklyIncome,
          salesExperience: formData.salesExperience,
          hasNoSalesExperience: formData.hasNoSalesExperience,
          rejectionHandling: formData.rejectionHandling,
          commissionMotivation: formData.commissionMotivation,
          voiceMessageLink: voiceLink,
          leadSource,
          trustedFormCertUrl
        })
      });

      if (response.ok) {
        hasSubmitted.current = true;
        clearSavedFormData(); // Clear localStorage on successful submit
        if (typeof window.gtag === "function") {
          window.gtag("event", "form_complete", {
            lead_source: leadSource,
          });
        }
        // Store first name for success page
        sessionStorage.setItem("applicantFirstName", firstName);
        // Navigate to success page
        window.location.href = "/success";
      } else {
        throw new Error("Submission failed");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("There was an error submitting your application. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const slideVariants = {
    enter: (direction: number) => ({ x: direction > 0 ? 50 : -50, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (direction: number) => ({ x: direction > 0 ? -50 : 50, opacity: 0 })
  };

  const inputClassName = "mt-1 text-slate-900 placeholder:text-gray-300 bg-white !border-2 !border-slate-200 focus:!border-emerald-500 focus:!ring-0 focus:!outline-none focus-visible:!outline-none focus-visible:!ring-0 focus-visible:!border-emerald-500 !shadow-none";

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
      {/* Header */}
      <header className="px-5 md:px-10 py-5 border-b border-white/10">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <a href="/">
            <img src={logo} alt="Empire Insurance" className="h-10 md:h-12" />
          </a>
          <a
            href="/"
            className="text-sm text-gray-400 hover:text-white transition-colors"
          >
            Back to Home
          </a>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-8 md:py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="p-6">
              <AnimatePresence mode="wait">
                {/* Disqualified State */}
                {isDisqualified ? (
                  <motion.div
                    key="disqualified"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-8"
                  >
                    <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <XCircle className="w-8 h-8 text-slate-500" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Not the Right Fit</h3>
                    <p className="text-gray-600 mb-6 text-sm leading-relaxed">
                      {disqualifyReason}
                    </p>
                    <a
                      href="/"
                      className="inline-block px-6 py-2 bg-slate-200 text-slate-700 rounded-full font-semibold hover:bg-slate-300 transition-colors"
                    >
                      Back to Home
                    </a>
                  </motion.div>
                ) : (
                  /* Form */
                  <motion.div key="form">
                    {/* Header */}
                    <div className="text-center mb-6">
                      <h2 className="text-xl font-bold text-gray-900">Quick Application</h2>
                      <p className="text-sm text-gray-500 mt-1">Step {currentStep} of {steps.length}</p>
                    </div>

                    {/* Progress bar */}
                    <div className="flex gap-2 mb-6">
                      {steps.map((step) => (
                        <div
                          key={step.id}
                          className={`h-1 flex-1 rounded-full transition-colors ${
                            currentStep >= step.id ? "bg-emerald-500" : "bg-gray-200"
                          }`}
                        />
                      ))}
                    </div>

                    {/* Form Steps */}
                    <form
                      onSubmit={(e) => e.preventDefault()}
                      id="empire-application-form"
                      data-tf-element-role="offer"
                    >
                      <input type="hidden" name="xxTrustedFormCertUrl" id="xxTrustedFormCertUrl" />
                      <div className="min-h-[280px] overflow-hidden">
                        <AnimatePresence mode="wait" custom={direction}>
                        {/* Step 1: Contact Info */}
                        {currentStep === 1 && (
                          <motion.div
                            key="step1"
                            custom={direction}
                            variants={slideVariants}
                            initial="enter"
                            animate="center"
                            exit="exit"
                            transition={{ duration: 0.2 }}
                            className="space-y-4 overflow-hidden"
                          >
                            <div className="space-y-4">
                                <div>
                                  <Label htmlFor="fullName" className="text-gray-700 text-sm flex items-center gap-1">
                                    <User className="w-3 h-3" /> Name
                                  </Label>
                                  <Input
                                    id="fullName"
                                    name="name"
                                    value={formData.fullName}
                                    onChange={(e) => updateFormData("fullName", e.target.value.slice(0, 100))}
                                    placeholder="John Doe"
                                    maxLength={100}
                                    autoComplete="name"
                                    autoCapitalize="words"
                                    enterKeyHint="next"
                                    onKeyDown={focusNextInput}
                                    className={`${inputClassName} ${errors.fullName ? "border-red-500" : ""}`}
                                  />
                                  {errors.fullName && <p className="text-red-500 text-xs mt-1">{errors.fullName}</p>}
                                </div>
                                <div>
                                  <Label htmlFor="email" className="text-gray-700 text-sm flex items-center gap-1">
                                    <Mail className="w-3 h-3" /> Email
                                  </Label>
                                  <Input
                                    id="email"
                                    name="email"
                                    type="email"
                                    inputMode="email"
                                    value={formData.email}
                                    onChange={(e) => updateFormData("email", e.target.value.slice(0, 100))}
                                    placeholder="john@example.com"
                                    maxLength={100}
                                    autoComplete="email"
                                    autoCapitalize="none"
                                    enterKeyHint="next"
                                    onKeyDown={focusNextInput}
                                    className={`${inputClassName} ${errors.email ? "border-red-500" : ""}`}
                                  />
                                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                                </div>
                                <div>
                                  <Label htmlFor="phone" className="text-gray-700 text-sm flex items-center gap-1">
                                    <Phone className="w-3 h-3" /> Phone
                                  </Label>
                                  <Input
                                    id="phone"
                                    name="tel"
                                    type="tel"
                                    inputMode="tel"
                                    value={formData.phone}
                                    onChange={handlePhoneChange}
                                    placeholder="(555) 123-4567"
                                    autoComplete="tel"
                                    enterKeyHint="done"
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        e.preventDefault();
                                        e.currentTarget.blur();
                                      }
                                    }}
                                    className={`${inputClassName} ${errors.phone ? "border-red-500" : ""}`}
                                  />
                                  {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                                </div>
                              </div>
                          </motion.div>
                        )}

                        {/* Step 2: Work Requirements */}
                        {currentStep === 2 && (
                          <motion.div
                            key="step2"
                            custom={direction}
                            variants={slideVariants}
                            initial="enter"
                            animate="center"
                            exit="exit"
                            transition={{ duration: 0.2 }}
                            className="space-y-5"
                          >
                            <div>
                              <Label className="text-gray-700 text-sm mb-2 block">
                                Are you able to work full-time, in-office in Oakland Park?
                              </Label>
                              <div className="grid grid-cols-2 gap-2">
                                {yesNoOptions.map((option) => (
                                  <button
                                    key={option.value}
                                    type="button"
                                    onClick={() => updateFormData("canWorkFullTimeInOffice", option.value)}
                                    className={`p-3 rounded-xl border-2 font-medium transition-all text-sm ${
                                      formData.canWorkFullTimeInOffice === option.value
                                        ? option.value === "yes"
                                          ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                                          : "border-red-400 bg-red-50 text-red-700"
                                        : "border-gray-200 text-gray-600 hover:border-gray-300"
                                    }`}
                                  >
                                    {option.label}
                                  </button>
                                ))}
                              </div>
                            </div>

                            <div>
                              <Label className="text-gray-700 text-sm mb-2 block">
                                Are you comfortable working a 100% commission-based role?
                              </Label>
                              <div className="grid grid-cols-2 gap-2">
                                {yesNoOptions.map((option) => (
                                  <button
                                    key={option.value}
                                    type="button"
                                    onClick={() => updateFormData("comfortableCommissionRole", option.value)}
                                    className={`p-3 rounded-xl border-2 font-medium transition-all text-sm ${
                                      formData.comfortableCommissionRole === option.value
                                        ? option.value === "yes"
                                          ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                                          : "border-red-400 bg-red-50 text-red-700"
                                        : "border-gray-200 text-gray-600 hover:border-gray-300"
                                    }`}
                                  >
                                    {option.label}
                                  </button>
                                ))}
                              </div>
                            </div>

                            <div>
                              <Label className="text-gray-700 text-sm mb-2 block">
                                Are you able to work evenings and Saturdays as required?
                              </Label>
                              <div className="grid grid-cols-2 gap-2">
                                {yesNoOptions.map((option) => (
                                  <button
                                    key={option.value}
                                    type="button"
                                    onClick={() => updateFormData("canWorkEveningsSaturdays", option.value)}
                                    className={`p-3 rounded-xl border-2 font-medium transition-all text-sm ${
                                      formData.canWorkEveningsSaturdays === option.value
                                        ? option.value === "yes"
                                          ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                                          : "border-red-400 bg-red-50 text-red-700"
                                        : "border-gray-200 text-gray-600 hover:border-gray-300"
                                    }`}
                                  >
                                    {option.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </motion.div>
                        )}

                        {/* Step 3: Age And Income */}
                        {currentStep === 3 && (
                          <motion.div
                            key="step3"
                            custom={direction}
                            variants={slideVariants}
                            initial="enter"
                            animate="center"
                            exit="exit"
                            transition={{ duration: 0.2 }}
                            className="space-y-5"
                          >
                            <div>
                              <Label className="text-gray-700 text-sm mb-2 block">
                                What is your age group?
                              </Label>
                              <div className="grid grid-cols-2 gap-2">
                                {ageRangeOptions.map((option) => (
                                  <button
                                    key={option.value}
                                    type="button"
                                    onClick={() => updateFormData("ageRange", option.value)}
                                    className={`p-3 rounded-xl border-2 font-medium transition-all text-sm ${
                                      formData.ageRange === option.value
                                        ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                                        : "border-gray-200 text-gray-600 hover:border-gray-300"
                                    }`}
                                  >
                                    {option.label}
                                  </button>
                                ))}
                              </div>
                            </div>

                            <div>
                              <Label className="text-gray-700 text-sm mb-2 block">
                                What is your target weekly income?
                              </Label>
                              <div className="space-y-2">
                                {targetWeeklyIncomeOptions.map((option) => (
                                  <button
                                    key={option.value}
                                    type="button"
                                    onClick={() => updateFormData("targetWeeklyIncome", option.value)}
                                    className={`w-full p-3 rounded-xl border-2 font-medium transition-all text-sm text-left ${
                                      formData.targetWeeklyIncome === option.value
                                        ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                                        : "border-gray-200 text-gray-600 hover:border-gray-300"
                                    }`}
                                  >
                                    {option.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </motion.div>
                        )}

                        {/* Step 4: Sales Experience */}
                        {currentStep === 4 && (
                          <motion.div
                            key="step4"
                            custom={direction}
                            variants={slideVariants}
                            initial="enter"
                            animate="center"
                            exit="exit"
                            transition={{ duration: 0.2 }}
                            className="space-y-4"
                          >
                            <div>
                              <Label className="text-gray-700 text-sm mb-2 block">
                                Do you have any prior sales experience? If yes, briefly explain.
                              </Label>
                              <textarea
                                value={formData.salesExperience}
                                onChange={(e) => updateFormData("salesExperience", e.target.value.slice(0, 500))}
                                placeholder="Example: 2 years of phone sales in a call center, closing inbound and outbound leads."
                                maxLength={500}
                                rows={5}
                                disabled={formData.hasNoSalesExperience}
                                className={`w-full p-3 text-slate-900 placeholder:text-gray-300 bg-white border-2 border-slate-200 rounded-lg focus:border-emerald-500 focus:outline-none text-base resize-none ${
                                  formData.hasNoSalesExperience ? "opacity-50 cursor-not-allowed bg-gray-50" : ""
                                }`}
                              />
                              <p className="text-xs text-gray-400 mt-1 text-right">{formData.salesExperience.length}/500</p>

                              {/* OR Divider */}
                              <div className="flex items-center gap-3 my-3">
                                <div className="flex-1 h-px bg-gray-200" />
                                <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">or</span>
                                <div className="flex-1 h-px bg-gray-200" />
                              </div>

                              {/* No Experience Option */}
                              <label
                                className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                                  formData.hasNoSalesExperience
                                    ? "border-emerald-500 bg-emerald-50"
                                    : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={formData.hasNoSalesExperience}
                                  onChange={(e) => {
                                    updateFormData("hasNoSalesExperience", e.target.checked);
                                    if (e.target.checked) {
                                      updateFormData("salesExperience", "");
                                    }
                                  }}
                                  className="w-5 h-5 text-emerald-500 border-gray-300 rounded focus:ring-emerald-500"
                                />
                                <span className={`text-sm font-medium ${formData.hasNoSalesExperience ? "text-emerald-700" : "text-gray-600"}`}>
                                  I don't have any sales experience
                                </span>
                              </label>
                            </div>
                          </motion.div>
                        )}

                        {/* Step 5: Manual Review */}
                        {currentStep === 5 && (
                          <motion.div
                            key="step5"
                            custom={direction}
                            variants={slideVariants}
                            initial="enter"
                            animate="center"
                            exit="exit"
                            transition={{ duration: 0.2 }}
                            className="space-y-4"
                          >
                            <div>
                              <Label className="text-gray-700 text-sm mb-2 block">
                                How do you handle rejection in a sales environment?
                              </Label>
                              <textarea
                                value={formData.rejectionHandling}
                                onChange={(e) => updateFormData("rejectionHandling", e.target.value.slice(0, 500))}
                                placeholder="Describe your mindset and process when you hear no."
                                maxLength={500}
                                rows={4}
                                className="w-full mt-1 p-3 text-slate-900 placeholder:text-gray-300 bg-white border-2 border-slate-200 rounded-lg focus:border-emerald-500 focus:outline-none text-base resize-none"
                              />
                              <p className="text-xs text-gray-400 mt-1">{formData.rejectionHandling.length}/500</p>
                            </div>

                            <div>
                              <Label className="text-gray-700 text-sm mb-2 block">
                                Why do you want to work in a commission-based role instead of hourly/salary?
                              </Label>
                              <textarea
                                value={formData.commissionMotivation}
                                onChange={(e) => updateFormData("commissionMotivation", e.target.value.slice(0, 500))}
                                placeholder="Tell us why a commission structure appeals to you."
                                maxLength={500}
                                rows={4}
                                className="w-full mt-1 p-3 text-slate-900 placeholder:text-gray-300 bg-white border-2 border-slate-200 rounded-lg focus:border-emerald-500 focus:outline-none text-base resize-none"
                              />
                              <p className="text-xs text-gray-400 mt-1">{formData.commissionMotivation.length}/500</p>
                            </div>
                          </motion.div>
                        )}

                        {/* Step 6: Final */}
                        {currentStep === 6 && (
                          <motion.div
                            key="step6"
                            custom={direction}
                            variants={slideVariants}
                            initial="enter"
                            animate="center"
                            exit="exit"
                            transition={{ duration: 0.2 }}
                            className="space-y-5"
                          >
                            <div>
                              <Label className="text-gray-700 text-sm mb-1 block">
                                Record a 30-60 second voice message telling us about yourself and one challenge you've overcome.
                              </Label>
                              <p className="text-xs text-gray-500 mb-4">
                                Speak clearly and confidently as if you were on a call.
                              </p>

                              {/* Recording Interface */}
                              <div className="relative">
                                {/* Not Recording State - Initial */}
                                {!isRecording && !voicePreviewUrl && !isUploadingVoice && (
                                  <motion.div
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    className="border-2 border-dashed border-gray-200 rounded-2xl p-6 text-center bg-gradient-to-b from-gray-50 to-white"
                                  >
                                    <button
                                      type="button"
                                      onClick={startVoiceRecording}
                                      className="group relative mx-auto flex items-center justify-center w-20 h-20 rounded-full bg-emerald-500 hover:bg-emerald-600 transition-all duration-300 hover:scale-105 shadow-lg shadow-emerald-500/25"
                                    >
                                      <Mic className="w-8 h-8 text-white" />
                                      <span className="absolute inset-0 rounded-full border-2 border-emerald-400 animate-ping opacity-25" />
                                    </button>
                                    <p className="mt-4 text-sm font-medium text-gray-700">
                                      Tap to start recording
                                    </p>
                                    <p className="mt-1 text-xs text-gray-400">
                                      Aim for about 30-60 seconds
                                    </p>
                                  </motion.div>
                                )}

                                {/* Recording State */}
                                {isRecording && (
                                  <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className="border-2 border-red-200 rounded-2xl p-6 bg-gradient-to-b from-red-50 to-white"
                                  >
                                    <div className="flex flex-col items-center">
                                      {/* Circular Progress Timer */}
                                      <div className="relative w-24 h-24 mb-4">
                                        <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
                                          <circle
                                            cx="50"
                                            cy="50"
                                            r="45"
                                            fill="none"
                                            stroke="#fee2e2"
                                            strokeWidth="6"
                                          />
                                          <motion.circle
                                            cx="50"
                                            cy="50"
                                            r="45"
                                            fill="none"
                                            stroke={recordingSeconds >= minRecordingSeconds ? "#22c55e" : "#ef4444"}
                                            strokeWidth="6"
                                            strokeLinecap="round"
                                            strokeDasharray={2 * Math.PI * 45}
                                            strokeDashoffset={2 * Math.PI * 45 * (1 - recordingSeconds / maxRecordingSeconds)}
                                            transition={{ duration: 0.5 }}
                                          />
                                        </svg>
                                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                                          <span className="text-2xl font-bold text-gray-800">{recordingSeconds}</span>
                                          <span className="text-[10px] text-gray-500 uppercase tracking-wide">seconds</span>
                                        </div>
                                      </div>

                                      {/* Animated Waveform Bars */}
                                      <div className="flex items-end justify-center gap-1 h-8 mb-4">
                                        {[...Array(12)].map((_, i) => (
                                          <motion.div
                                            key={i}
                                            className="w-1 bg-red-400 rounded-full"
                                            animate={{
                                              height: [8, 24, 12, 28, 8],
                                            }}
                                            transition={{
                                              duration: 0.8,
                                              repeat: Infinity,
                                              delay: i * 0.1,
                                              ease: "easeInOut",
                                            }}
                                          />
                                        ))}
                                      </div>

                                      {/* Status Text */}
                                      <p className="text-sm text-gray-600 mb-1">
                                        Recording...
                                      </p>

                                      {/* Stop Button */}
                                      <button
                                        type="button"
                                        onClick={stopVoiceRecording}
                                        className="mt-4 flex items-center gap-2 px-5 py-2.5 rounded-full bg-red-500 hover:bg-red-600 text-white font-medium transition-colors shadow-lg shadow-red-500/25"
                                      >
                                        <Square className="w-4 h-4 fill-current" />
                                        Stop Recording
                                      </button>
                                    </div>
                                  </motion.div>
                                )}

                                {/* Uploading State */}
                                {isUploadingVoice && (
                                  <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className="border-2 border-emerald-200 rounded-2xl p-6 bg-gradient-to-b from-emerald-50 to-white"
                                  >
                                    <div className="flex flex-col items-center">
                                      <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                                        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
                                      </div>
                                      <p className="text-sm font-medium text-gray-700">Uploading your recording...</p>
                                      <p className="text-xs text-gray-500 mt-1">This will only take a moment</p>
                                    </div>
                                  </motion.div>
                                )}

                                {/* Preview State - Recording ready but not uploaded */}
                                {!!voicePreviewUrl && !isRecording && !isUploadingVoice && !formData.voiceMessageLink && (
                                  <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="border-2 border-emerald-200 rounded-2xl p-5 bg-gradient-to-b from-emerald-50 to-white"
                                  >
                                    {/* Ready Badge */}
                                    <div className="flex items-center gap-2 mb-4">
                                      <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center">
                                        <CheckCircle className="w-5 h-5 text-white" />
                                      </div>
                                      <div>
                                        <p className="text-sm font-semibold text-gray-800">Recording ready</p>
                                        <p className="text-xs text-gray-500">Listen to preview, then click Submit below</p>
                                      </div>
                                    </div>

                                    {/* Custom Audio Player */}
                                    <div className="bg-white rounded-xl p-3 border border-gray-100 shadow-sm">
                                      <audio
                                        ref={audioRef}
                                        src={voicePreviewUrl}
                                        onTimeUpdate={() => {
                                          if (audioRef.current) {
                                            setPlaybackProgress((audioRef.current.currentTime / audioRef.current.duration) * 100 || 0);
                                          }
                                        }}
                                        onEnded={() => {
                                          setIsPlaying(false);
                                          setPlaybackProgress(0);
                                        }}
                                        className="hidden"
                                      />
                                      <div className="flex items-center gap-3">
                                        <button
                                          type="button"
                                          onClick={() => {
                                            if (audioRef.current) {
                                              if (isPlaying) {
                                                audioRef.current.pause();
                                              } else {
                                                audioRef.current.play();
                                              }
                                              setIsPlaying(!isPlaying);
                                            }
                                          }}
                                          className="w-10 h-10 rounded-full bg-emerald-500 hover:bg-emerald-600 flex items-center justify-center transition-colors flex-shrink-0"
                                        >
                                          {isPlaying ? (
                                            <Pause className="w-4 h-4 text-white fill-current" />
                                          ) : (
                                            <Play className="w-4 h-4 text-white fill-current ml-0.5" />
                                          )}
                                        </button>
                                        <div className="flex-1">
                                          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                            <motion.div
                                              className="h-full bg-emerald-500 rounded-full"
                                              style={{ width: `${playbackProgress}%` }}
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Re-record Option */}
                                    <button
                                      type="button"
                                      onClick={startVoiceRecording}
                                      disabled={isSubmitting}
                                      className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      <RotateCcw className="w-4 h-4" />
                                      Record again
                                    </button>
                                  </motion.div>
                                )}

                                {/* Uploaded State - Recording submitted */}
                                {!!formData.voiceMessageLink && !isRecording && !isUploadingVoice && (
                                  <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="border-2 border-emerald-200 rounded-2xl p-5 bg-gradient-to-b from-emerald-50 to-white"
                                  >
                                    {/* Success Badge */}
                                    <div className="flex items-center gap-2 mb-4">
                                      <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center">
                                        <CheckCircle className="w-5 h-5 text-white" />
                                      </div>
                                      <div>
                                        <p className="text-sm font-semibold text-gray-800">Recording submitted</p>
                                        <p className="text-xs text-gray-500">You're ready to submit your application</p>
                                      </div>
                                    </div>

                                    {/* Custom Audio Player */}
                                    {!!voicePreviewUrl && (
                                      <div className="bg-white rounded-xl p-3 border border-gray-100 shadow-sm">
                                        <audio
                                          ref={audioRef}
                                          src={voicePreviewUrl}
                                          onTimeUpdate={() => {
                                            if (audioRef.current) {
                                              setPlaybackProgress((audioRef.current.currentTime / audioRef.current.duration) * 100 || 0);
                                            }
                                          }}
                                          onEnded={() => {
                                            setIsPlaying(false);
                                            setPlaybackProgress(0);
                                          }}
                                          className="hidden"
                                        />
                                        <div className="flex items-center gap-3">
                                          <button
                                            type="button"
                                            onClick={() => {
                                              if (audioRef.current) {
                                                if (isPlaying) {
                                                  audioRef.current.pause();
                                                } else {
                                                  audioRef.current.play();
                                                }
                                                setIsPlaying(!isPlaying);
                                              }
                                            }}
                                            className="w-10 h-10 rounded-full bg-emerald-500 hover:bg-emerald-600 flex items-center justify-center transition-colors flex-shrink-0"
                                          >
                                            {isPlaying ? (
                                              <Pause className="w-4 h-4 text-white fill-current" />
                                            ) : (
                                              <Play className="w-4 h-4 text-white fill-current ml-0.5" />
                                            )}
                                          </button>
                                          <div className="flex-1">
                                            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                              <motion.div
                                                className="h-full bg-emerald-500 rounded-full"
                                                style={{ width: `${playbackProgress}%` }}
                                              />
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    )}

                                    {/* Re-record Option */}
                                    <button
                                      type="button"
                                      onClick={startVoiceRecording}
                                      className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-colors text-sm font-medium"
                                    >
                                      <RotateCcw className="w-4 h-4" />
                                      Record a new one
                                    </button>
                                  </motion.div>
                                )}

                                {/* Error Message */}
                                {voiceUploadError && (
                                  <motion.div
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="mt-3 p-3 rounded-xl bg-red-50 border border-red-200"
                                  >
                                    <p className="text-sm text-red-600 flex items-center gap-2">
                                      <XCircle className="w-4 h-4 flex-shrink-0" />
                                      {voiceUploadError}
                                    </p>
                                  </motion.div>
                                )}
                              </div>
                            </div>

                            {/* TCPA Consent disclosure with TrustedForm tagging */}
                            <p
                              className="text-xs text-gray-500 leading-relaxed text-center"
                              data-tf-element-role="consent-language"
                            >
                              By clicking Submit, I consent to receive calls and texts from{" "}
                              <span data-tf-element-role="consent-advertiser-name">Empire Insurance Agency</span>
                              {" "}at the phone number provided, including via automated technology. Consent is not
                              required to apply. Msg & data rates may apply. View our{" "}
                              <a href="/privacy" className="text-blue-600 underline" target="_blank" rel="noopener noreferrer">
                                Privacy Policy
                              </a>{" "}
                              and{" "}
                              <a href="/terms" className="text-blue-600 underline" target="_blank" rel="noopener noreferrer">
                                Terms
                              </a>.
                            </p>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                    {/* Navigation */}
                    <div className="flex items-center justify-between mt-6 pt-4 border-t">
                      <button
                        type="button"
                        onClick={prevStep}
                        disabled={currentStep === 1}
                        className={`flex items-center gap-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                          currentStep === 1
                            ? "text-gray-300 cursor-not-allowed"
                            : "text-gray-600 hover:bg-gray-100"
                        }`}
                      >
                        <ArrowLeft className="w-4 h-4" /> Back
                      </button>

                      {currentStep < steps.length ? (
                        <button
                          type="button"
                          onClick={nextStep}
                          disabled={!canProceed()}
                          className={`flex items-center gap-1 px-6 py-2 rounded-full font-semibold transition-colors ${
                            canProceed()
                              ? "bg-emerald-500 text-white hover:bg-emerald-600"
                              : "bg-gray-200 text-gray-400 cursor-not-allowed"
                          }`}
                        >
                          Next <ArrowRight className="w-4 h-4" />
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={handleSubmit}
                          disabled={!canProceed() || isSubmitting || isUploadingVoice}
                          data-tf-element-role="submit"
                          className={`flex items-center gap-1 px-6 py-2 rounded-full font-semibold transition-colors ${
                            canProceed() && !isSubmitting && !isUploadingVoice
                              ? "bg-emerald-500 text-white hover:bg-emerald-600"
                              : "bg-gray-200 text-gray-400 cursor-not-allowed"
                          }`}
                        >
                          {isUploadingVoice ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" /> Uploading recording...
                            </>
                          ) : isSubmitting ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" /> Submitting...
                            </>
                          ) : (
                            <>
                              Submit <CheckCircle className="w-4 h-4" />
                            </>
                          )}
                        </button>
                      )}
                    </div>
                    </form>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="px-5 md:px-10 lg:px-16 py-10 border-t border-white/10">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-8">
            <img src={logo} alt="Empire Insurance" className="h-10" />
            <div className="flex items-center gap-6">
              <a href="/privacy" className="text-sm text-gray-500 hover:text-white transition-colors">
                Privacy Policy
              </a>
              <a href="/terms" className="text-sm text-gray-500 hover:text-white transition-colors">
                Terms of Use
              </a>
            </div>
          </div>

          <div className="border-t border-white/10 pt-6 space-y-4">
            <p className="text-xs text-gray-600 leading-relaxed text-center">
              This site is not a part of the Facebook™ website or Facebook™ Inc. Additionally, this site is NOT endorsed by Facebook™ in any way. FACEBOOK™ is a trademark of FACEBOOK™, Inc. Meta™ and the Meta™ logo are trademarks of Meta Platforms, Inc.
            </p>
            <p className="text-xs text-gray-600 leading-relaxed text-center">
              The income figures stated ($8K-$15K/month) represent earnings of top-performing agents and are not typical or guaranteed. Your results will vary and depend on many factors including but not limited to your background, experience, work ethic, and market conditions. All business entails risk and requires consistent effort and action. This is a commission-only position with no guaranteed base salary.
            </p>
            <p className="text-sm text-gray-600 text-center">
              © {new Date().getFullYear()} Empire Insurance Agency. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

    </div>
  );
}
